// Shared layout + typography for the legal pages (CGU, Politique de
// Confidentialité). Matches the landing aesthetic (dark + acid-lime).
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Logo } from "../assets/logo.svg";

const AC = "#B4FF3A";
const BG = "#070707";

// --- Reusable content blocks (exported for the page files) ---
export const Section = ({ n, title, children }) => (
  <section style={{ marginBottom: 38 }}>
    <h2 className="bc" style={{ fontSize: "clamp(22px,2.8vw,32px)", fontWeight: 800, color: "#F2F2F2", textTransform: "uppercase", letterSpacing: "-.5px", marginBottom: 16, display: "flex", gap: 12, alignItems: "baseline" }}>
      {n != null && <span style={{ color: AC, fontSize: "0.7em" }}>{String(n).padStart(2, "0")}</span>}
      {title}
    </h2>
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
  </section>
);

export const P = ({ children }) => (
  <p style={{ fontSize: "clamp(15px,1.5vw,17px)", lineHeight: 1.8, color: "#9A9A9A", fontWeight: 400 }}>{children}</p>
);

export const B = ({ children }) => <strong style={{ color: "#E8E8E8", fontWeight: 600 }}>{children}</strong>;

export const UL = ({ children }) => (
  <ul style={{ display: "flex", flexDirection: "column", gap: 9, paddingLeft: 4, listStyle: "none" }}>{children}</ul>
);

export const LI = ({ children }) => (
  <li style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
    <span style={{ color: AC, marginTop: 7, width: 5, height: 5, borderRadius: "50%", background: AC, flexShrink: 0 }} />
    <span style={{ fontSize: "clamp(15px,1.5vw,17px)", lineHeight: 1.75, color: "#9A9A9A" }}>{children}</span>
  </li>
);

// Simple sub-processor / data table
export const Table = ({ head, rows }) => (
  <div style={{ overflowX: "auto", border: "1px solid rgba(255,255,255,.08)", borderRadius: 12, marginTop: 4 }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
      <thead>
        <tr style={{ background: "rgba(255,255,255,.03)" }}>
          {head.map((h) => (
            <th key={h} style={{ textAlign: "left", padding: "12px 14px", color: "#C8C8C8", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: ".05em", borderBottom: "1px solid rgba(255,255,255,.08)" }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ borderBottom: i < rows.length - 1 ? "1px solid rgba(255,255,255,.05)" : "none" }}>
            {r.map((c, j) => (
              <td key={j} style={{ padding: "11px 14px", color: j === 0 ? "#E8E8E8" : "#8A8A8A", lineHeight: 1.5, verticalAlign: "top" }}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const LegalPage = ({ title, subtitle, lastUpdated, children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Manrope:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
    window.scrollTo(0, 0);
    return () => { try { document.head.removeChild(link); } catch (_) {} };
  }, []);

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif", background: BG, color: "#F2F2F2", minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        .bc { font-family: 'Barlow Condensed', sans-serif; }
        .lk { color: ${AC}; text-decoration: none; border-bottom: 1px solid rgba(180,255,58,.3); }
        .lk:hover { border-bottom-color: ${AC}; }
        .bo { background:transparent; border:1px solid rgba(255,255,255,.14); color:#F2F2F2; cursor:pointer; font-family:'Manrope',sans-serif; font-weight:600; transition:all .22s; }
        .bo:hover { border-color:rgba(255,255,255,.4); background:rgba(255,255,255,.04); }
      `}</style>

      {/* NAV */}
      <nav style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(7,7,7,.9)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <div style={{ maxWidth: 920, margin: "0 auto", padding: "16px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => navigate("/")}>
            <Logo style={{ height: 24 }} />
            <span className="bc" style={{ fontSize: 19, fontWeight: 900, letterSpacing: "1px", textTransform: "uppercase" }}>1PARC</span>
          </div>
          <button className="bo" style={{ padding: "9px 18px", borderRadius: 8, fontSize: 13 }} onClick={() => navigate("/")}>← Accueil</button>
        </div>
      </nav>

      {/* HEADER */}
      <header style={{ maxWidth: 820, margin: "0 auto", padding: "64px 28px 24px" }}>
        <h1 className="bc" style={{ fontSize: "clamp(34px,5vw,60px)", fontWeight: 900, color: "#F2F2F2", textTransform: "uppercase", letterSpacing: "-1.5px", lineHeight: .98 }}>{title}</h1>
        {subtitle && <p style={{ color: "#8A8A8A", fontSize: 16, marginTop: 14, lineHeight: 1.7 }}>{subtitle}</p>}
        <p style={{ color: "#555", fontSize: 13, marginTop: 18 }}>Dernière mise à jour : {lastUpdated}</p>
        <div style={{ width: 44, height: 2, background: AC, marginTop: 22 }} />
      </header>

      {/* BODY */}
      <main style={{ maxWidth: 820, margin: "0 auto", padding: "20px 28px 40px" }}>{children}</main>

      {/* FOOTER */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,.06)", background: "#040404" }}>
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "32px 28px", display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
            <button className="bc" onClick={() => navigate("/conditions")} style={{ background: "none", border: "none", color: "#8A8A8A", cursor: "pointer", fontSize: 13, fontFamily: "'Manrope',sans-serif" }}>Conditions d'utilisation</button>
            <button className="bc" onClick={() => navigate("/confidentialite")} style={{ background: "none", border: "none", color: "#8A8A8A", cursor: "pointer", fontSize: 13, fontFamily: "'Manrope',sans-serif" }}>Politique de confidentialité</button>
            <button onClick={() => navigate("/")} style={{ background: "none", border: "none", color: "#8A8A8A", cursor: "pointer", fontSize: 13, fontFamily: "'Manrope',sans-serif" }}>Accueil</button>
          </div>
          <span style={{ fontSize: 11, color: "#333", fontWeight: 700, letterSpacing: ".05em" }}>© {new Date().getFullYear()} 1 PARC SARL</span>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
